import assert from 'assert-ts';
import get from 'lodash/get';
import type {Concept} from 'types';
import type {ChangeRequestAction, DataValue} from 'schemaDefinition/types';
import type {
  ExpressionV4,
  ManifestationV4,
  Metadata,
  MetadataChanges,
  MetadataChangesInfo,
  WorkV4,
} from '../types';
import {appendItemToOriginalValue} from './appendItemToOriginalValue';
import {getChangeRequestRootAndLocalPath} from './getChangeRequestRootAndLocalPath';
import {getMetadataEntity} from './getMetadataEntity';
import {getParentArrayPathParts} from './getParentArrayPathParts';
import {removeItemFromOriginalValue} from './removeItemFromOriginalValue';
import {replaceOriginalValue} from './replaceOriginalValue';
import {resolveApprovedValueTarget} from './resolveApprovedValueTarget';

export const addApproval = (
  changeRequestId: string,
  rootValuePath: string,
  itemAction: ChangeRequestAction,
  newValue: DataValue | undefined,
  statusPath: string | undefined,
  approvals: MetadataChanges,
  metadata: Metadata,
): {
  approvals: MetadataChanges;
  updatedEntity: WorkV4 | ExpressionV4 | ManifestationV4;
  entityType: Concept.work | Concept.expression | Concept.manifestation;
  entityId: string;
} => {
  const {root, localPath} = getChangeRequestRootAndLocalPath(rootValuePath);
  const changeRequest = assert(
    (metadata.changeRequests ?? []).find(cr => cr.id === changeRequestId),
    'addApproval: changeRequest not found',
    {changeRequestId},
  );

  const entityId = changeRequest[root].id;
  const value =
    newValue !== undefined
      ? newValue
      : ((get(changeRequest, rootValuePath) ?? null) as DataValue);

  let updatedHistory = approvals[changeRequestId]?.arraysUpdateHistory ?? {};

  const {entity, entityType} = getMetadataEntity(entityId, metadata);
  let updatedEntity = entity;

  // If newValue is provided, replace, independent of itemAction
  const resolvedItemAction = newValue !== undefined ? 'replace' : itemAction;

  // Resolve where in the original entity the value should be applied, as
  // indexes of the change request need not match those of the original entity
  const target =
    resolvedItemAction === 'replace'
      ? resolveApprovedValueTarget(
          entity,
          changeRequest[root],
          localPath,
          value,
        )
      : {itemAction: resolvedItemAction, localPath, value};

  switch (target.itemAction) {
    case 'replace':
      updatedEntity = replaceOriginalValue(
        entity,
        target.localPath,
        target.value,
      );
      break;
    case 'removeItem':
    case 'addItem': {
      const {parentPath} = getParentArrayPathParts(target.localPath);
      if (
        !assert.soft(
          parentPath,
          'addItemToOriginalValue: parentArrayPath not found',
        )
      ) {
        break;
      }

      const arrayHistory = updatedHistory[parentPath] ?? [];
      const updated =
        target.itemAction === 'addItem'
          ? appendItemToOriginalValue(
              entity,
              arrayHistory,
              target.localPath,
              target.value,
            )
          : removeItemFromOriginalValue(entity, arrayHistory, target.localPath);
      updatedEntity = updated.entity;
      updatedHistory = {
        ...updatedHistory,
        [parentPath]: updated.history ?? [],
      };
      break;
    }
    default:
      assert(false, 'addApproval: unknown itemAction', {itemAction});
  }

  const changeRequestApprovals: MetadataChangesInfo = {
    rootPaths: approvals[changeRequestId]?.rootPaths ?? [],
    arraysUpdateHistory: updatedHistory,
  };

  // Add statusPath if provided, otherwise use rootValuePath
  changeRequestApprovals.rootPaths.push(statusPath ?? rootValuePath);

  const updatedApprovals: MetadataChanges = {
    ...approvals,
    [changeRequestId]: changeRequestApprovals,
  };

  return {approvals: updatedApprovals, updatedEntity, entityType, entityId};
};
